import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Textarea } from '@/shared/ui/atoms';
import { FormField } from '@/shared/ui/molecules';

import { CouponAutocomplete } from '../../molecules/LookupAutocomplete';
import { PlanFormModal } from '../../molecules/PlanFormModal';

/** Props de `AddCouponModal`. */
export interface AddCouponModalProps {
  /** Controla a visibilidade. */
  readonly open: boolean;
  /** `true` enquanto a aplicação está em voo. */
  readonly isPending: boolean;
  /** Aplica o cupom — chama endpoint na hora, sem depender do Salvar. */
  readonly onApply: (input: { readonly couponId: number; readonly description: string }) => void;
  /** Fecha sem aplicar. */
  readonly onClose: () => void;
}

/**
 * Modal de aplicação de cupom de desconto.
 *
 * **Chama endpoint na hora**, ao contrário do modal de desconto: cupom tem
 * `POST .../coupons` próprio e persiste imediatamente, invalidando o editor.
 * Essa assimetria é declarada no texto do modal para o operador não supor que a
 * aplicação depende do Salvar do plano.
 *
 * A justificativa vai como `description` — é ela que alimenta a coluna "Motivo
 * da inserção" do histórico de cupons.
 *
 * Fica sempre montado e é fechado por `open → false`: desmontar um dialog Radix
 * aberto deixa `pointer-events: none` no `body` e trava a página.
 */
export function AddCouponModal({ open, isPending, onApply, onClose }: AddCouponModalProps) {
  const { t } = useTranslation('individualPayment');

  const [couponId, setCouponId] = useState('');
  const [couponLabel, setCouponLabel] = useState('');
  const [justification, setJustification] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const errors = {
    coupon: couponId === '',
    justification: justification.trim() === '',
  };
  const hasError = Object.values(errors).some(Boolean);

  const reset = () => {
    setCouponId('');
    setCouponLabel('');
    setJustification('');
    setSubmitted(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleConfirm = () => {
    setSubmitted(true);
    if (hasError) return;

    onApply({ couponId: Number(couponId), description: justification.trim() });
  };

  const requiredMessage = t('planEdit.validation.required');

  return (
    <PlanFormModal
      open={open}
      title={t('planEdit.values.coupons.modalTitle')}
      description={t('planEdit.values.coupons.immediate')}
      confirmLabel={t('planEdit.values.coupons.modalConfirm')}
      isPending={isPending}
      onConfirm={handleConfirm}
      onClose={close}
    >
      <CouponAutocomplete
        label={t('planEdit.values.coupons.modalCoupon')}
        placeholder={t('planEdit.values.coupons.modalCouponPlaceholder')}
        value={couponId}
        selectedLabel={couponLabel}
        onChange={(id, label) => {
          setCouponId(id);
          setCouponLabel(label);
        }}
      />
      {submitted && errors.coupon ? (
        <span className="ts-paragraph-xxs text-base-danger-700">{requiredMessage}</span>
      ) : null}

      <FormField
        label={t('planEdit.values.coupons.modalJustification')}
        required
        {...(submitted && errors.justification ? { error: requiredMessage } : {})}
      >
        <Textarea
          rows={3}
          placeholder={t('planEdit.values.coupons.modalJustificationPlaceholder')}
          value={justification}
          onChange={event => setJustification(event.target.value)}
        />
      </FormField>
    </PlanFormModal>
  );
}
